/**
 * RePrescribeModal.tsx — ⚠️ TEMPORARY. The v1 backlog's re-prescribe surface.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: prescribing truth
 * (2026-09-12).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHAT IT IS FOR, AND WHY IT IS TEMPORARY.
 *
 * A finite set of orders carry a prescription that records NO prescriber-chosen
 * strength: the deleted auto-issue lane minted them from the ORDER, inside the
 * approve click, with no prescriber input at all. They cannot be dispensed —
 * orders-service refuses the pharmacy send on `correctlyPrescribed = false` —
 * and they cannot be repaired by the ordinary Prescribe flow's affordances,
 * because that screen is built for an order with no prescription and these
 * orders have one.
 *
 * So this modal exists for exactly those cards, and it is the CTO's own shape:
 * "add a temp flag something showing re approval needed. so he need to re
 * prescibe (give a button) and it opens a temp modal just for these cards."
 *
 * WHEN IT GOES: when `needsPrescriptionReapproval` is false for every order in
 * the book. Delete this file, its chip (`REAPPROVAL_CHIP` / `REAPPROVAL_REASON`
 * in prescribingFlags.tsx), its mount in ReviewView, and orders-service's
 * `needsPrescriptionReapproval` key + `reapproval.ts` + the `v1Deal` column.
 * `awaitingPrescription`, `PrescribeForm` and the picker STAY — they are the
 * ordinary flow. Written down in CHANGELOG.md too, because a temporary thing
 * with no stated removal is a permanent thing with an apology attached.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT USES THE ORDINARY PRESCRIBE ENDPOINT. NOT A LANE OF ITS OWN.
 *
 * The previous attempt at this (`MedicationCorrection`, deleted today) POSTed to
 * a bespoke `/medication-amendment` route. orders-service deleted that route in
 * the same slice, and the button stayed mounted for every clinician on every
 * case with a prescription — a 404 on the exact screen, for the exact defect.
 * That is the cost of a second lane, paid within one day of building it.
 *
 * So this posts `POST /v1/orders/:id/prescribe`, the same call the ordinary form
 * makes, and inherits every gate that route already carries: approved, captured,
 * prescriber identified, a chosen strength. One lane, one set of rules.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE MONEY SENTENCE COMES BEFORE THE DECISION, NOT BESIDE THE BUTTON.
 *
 * A clinician must never have to guess whether a click charges a patient. It is
 * stated in the panel, above the controls, and AGAIN in the confirm dialog —
 * because the confirm is the last thing read before a signed document is minted,
 * and a sentence only shown on the screen behind it has been read before the
 * decision was formed, not while it was being taken.
 *
 * AND IT IS STRUCTURALLY TRUE, not a promise this file makes. `prescribeWithinTx`
 * in orders-service holds no Stripe client at all, and its tests enumerate every
 * method on the Stripe port by reflection and assert zero calls — in BOTH payment
 * states. The month-1 capture happens once, on approval, and nothing here moves,
 * repeats or adds one (I2).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DOUBLE-SUBMIT. Two signed prescriptions for one patient is the worst thing
 * this surface could cause, so the confirm is guarded twice: the dialog's own
 * `busy` disables it, and an in-flight ref refuses a second call outright even
 * if a dialog somehow fires twice in one tick. A ref, not the `busy` state —
 * two clicks in the same tick both read the PRE-update state value, which is
 * how a "disabled while busy" button still posts twice.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SUPPLIES — THE SAME SECTION AS THE ORDINARY FORM, AND ALWAYS POSTED
 * (review fix, 2026-09-24).
 *
 * This modal used to OMIT `supplies`, on the reasoning that orders carries the
 * previous prescription's supplies forward when the key is absent. That was
 * exactly wrong for the documents this modal exists for: every v1 backlog
 * prescription PREDATES the supplies column, so its stored supplies are NULL,
 * the carry-forward is `[]`, and a re-saved injectable went to the pharmacy
 * with nothing to use it with — with no control on screen that could stop it.
 * The rule is that supplies can be added to ANY prescription, and this is one.
 *
 * So the modal mounts the SAME `SuppliesSection` over the SAME `useSupplyRows`
 * as `PrescribeForm`: seeded from the prior prescription's supplies FIRST, then
 * every server suggestion that list does not already carry (a supply on both
 * keeps the PRIOR quantity — `supplyRows.ts#seedSupplyRows`); Continue and
 * Re-save blocked while
 * the read is loading or failed (or `suppliesAvailable` is false), with the same
 * alert and Retry; the confirm step lists the supplies or says none will be
 * sent; and `supplies` is ALWAYS on the body, `[]` when every row was removed.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * REQUIRED FIELDS ARE MARKED, AND A BLOCKED "Continue" SAYS WHAT IS MISSING
 * (2026-09-25, CTO) — the same treatment as `PrescribeForm`, from the same
 * `prescribeRequirements.ts`: a "*" (+ `aria-required`) on Strength, Directions
 * and Quantity under one legend, a "Still needed: …" line while anything is
 * missing, and an `aria-disabled` Continue whose press shows the error summary
 * and moves focus to it. Continue only swaps this dialog's body for the confirm
 * step, so letting it be pressed moves nothing; "Re-save and send" is unchanged.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * R28: no medicine or supply name is hard-coded here — every name on screen
 * arrives at runtime from the formulary or the prescribing context. R19:
 * nothing here is logged.
 */
import { useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import { prescribe } from '../api/orders';
import type { PrescribedItem, PrescriptionItemInput } from '../api/types';
import { ErrorSummary, StillNeededHint, inlineErrorFor, invalidControlStyle, type MissingItem } from '../ui/ErrorSummary';
import { Alert, Button, ErrorLine, ErrorState, Field, Help, TextInput } from '../ui/kit';
import { DialogShell } from '../ui/ConfirmDialog';
import { RequiredLegend } from '../ui/RequiredMark';
import { DirectionsCounter } from './DirectionsCounter';
import { directionsWithinLimit, lineRequirements, shownErrors, supplyRequirements } from './prescribeRequirements';
import { useFormulary } from './useFormulary';
import { VariantPicker } from './VariantPicker';
import { paymentTakenForCopy } from './paymentTaken';
import { seedDirections } from '../lib/prescribedLine';
import { SupplyLines } from '../ui/PrescribedSupplies';
import { NO_SUPPLIES_COPY, SuppliesSection, supplySectionIds } from './SuppliesSection';
import { suppliesForWire, suppliesSummary } from './supplyRows';
import { useSupplyRows } from './usePrescribingContext';

/** The four words that lead every money branch. Exported so the specs pin them. */
export const NO_PAYMENT_LEAD = 'No payment will be taken.';

/**
 * WHAT A CORRECTION ACTUALLY DOES TO THE PHARMACY, AND NOTHING MORE (2026-09-13).
 *
 * Both steps used to say "the pharmacy is sent the new document". They are not:
 * a correction runs SEND-FREE — no dispensing request is transmitted and no email
 * is raised — because the pharmacy already holds this supply, which is the CTO's
 * explicit rule for this lane. What DOES happen is that the strength-less document
 * is superseded and the order returns to the pharmacy's own queue, where they read
 * the corrected item and dispense from it.
 *
 * Exported so a spec can pin them: telling a prescriber their document was
 * transmitted when it was not is the same class of defect as printing a strength
 * nobody chose, which is the reason this lane exists at all.
 */
export const CORRECTION_PHARMACY_EFFECT =
  'The pharmacy is not emailed — the corrected item appears in their queue, where they dispense from it.';
export const CORRECTION_PHARMACY_EFFECT_SHORT = 'the corrected item then appears in the pharmacy\u2019s queue';

/**
 * The money sentence, in the clinician's own terms.
 *
 * EVERY branch leads with {@link NO_PAYMENT_LEAD}, because it is true in every
 * branch — re-prescribing performs no Stripe call whatever state the payment is
 * in. The rest only tells the clinician where the patient stands, and the
 * unknown branch says it is unknown rather than picking the likelier of two
 * claims about somebody's money (see `paymentTaken.ts`: a hint for copy, never
 * a control).
 */
export function chargeCopy(paymentTaken: boolean | null): string {
  if (paymentTaken === true) {
    return `${NO_PAYMENT_LEAD} This patient has already paid for this order, and re-prescribing does not change the price or charge the card again.`;
  }
  if (paymentTaken === false) {
    return `${NO_PAYMENT_LEAD} Re-prescribing cannot charge a card. The payment for this order has not been taken yet, so it may be refused until it has been — nothing will be charged either way.`;
  }
  return `${NO_PAYMENT_LEAD} Re-prescribing cannot charge a card, whatever stage the payment is at.`;
}

/**
 * The honest sentence for the ONE refusal a clinician cannot act on from here.
 *
 * `prescribeWithinTx` asserts a CAPTURED payment record exists before it writes
 * anything. On an order that never completed checkout that assert throws, and
 * the clinician needs to be told what is actually true — the patient has not
 * paid — rather than "please try again", which is wrong: an identical retry
 * refuses identically.
 */
export const NO_PAYMENT_REFUSAL =
  'No payment has been taken on this order, so it cannot be prescribed. The patient needs to choose a plan and pay first — nothing you do on this screen can move it forward.';

/** The house line, for a refusal with nothing readable on it. */
export const REPRESCRIBE_FAILED =
  'Sorry — the prescription could not be issued. Nothing has been changed.';

/**
 * Turn a failed prescribe into a sentence a clinician can act on.
 *
 * THREE BRANCHES, IN THIS ORDER AND FOR THIS REASON:
 *  1. The 409 that means "there is no captured payment" gets OUR plain-English
 *     sentence, because the server's own wording names an internal error class
 *     and tells a clinician nothing about the patient's situation.
 *  2. Any other refusal carrying a detail is relayed VERBATIM. Six distinct
 *     refusals land on this route and each one is a different next move; the
 *     gateway relays the server's sentence and it is better than anything this
 *     file could paraphrase (R41 — the server is the control, including over
 *     its own words).
 *  3. Anything else — a network wobble, an unparsed body — gets the house line,
 *     which says nothing changed rather than guessing.
 *
 * ⚠️ NEVER "please try again" on a 409. The order's STATE is refusing, so an
 * identical retry refuses identically. On production a clinician was told to
 * try again on an already-sent prescription and did — twice.
 */
export function refusalCopy(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.status === 409 && /payment/i.test(cause.detail ?? '')) return NO_PAYMENT_REFUSAL;
    if (cause.detail) return cause.detail;
  }
  return REPRESCRIBE_FAILED;
}

/**
 * WHERE THE STARTING LINES COME FROM — A STATE, NOT AN ARRAY (2026-09-13).
 *
 * ⚠️ THIS IS THE DEAD-END FIX, AND IT IS THE WHOLE REASON THIS TYPE EXISTS.
 *
 * The control that opens this modal is gated on `needsPrescriptionReapproval`,
 * which rides the ORDER — a row the drawer already has. The modal's editors were
 * built from a DIFFERENT read, `GET /v1/orders/:id/prescriptions`, in a one-shot
 * `useState` initializer, and ReviewView handed it `latestPrescription?.items ?? []`.
 * So on the two reads disagreeing — the history still in flight, or failed, or a
 * document whose `items` an older orders build omits — the modal opened with its
 * two alerts, ZERO strength pickers, ZERO Directions fields, and a Continue that
 * `ready` (which demands `drafts.length > 0`) disabled FOREVER. A clinician under
 * time pressure clicked the button built for him and got a dialog with no way
 * forward and nothing saying why. This estate has a named history of shipping
 * controls with no way to click them; this was one with no way to finish.
 *
 * The array could not express any of that, so it is a STATE. `loading` and
 * `error` are real answers rather than "no lines", and the editors are mounted
 * by a SEPARATE COMPONENT (`RePrescribeForm`) that only exists on the `ready`
 * branch with at least one line. That is structural, not a guard: there is no
 * code path on which the draft editors render against an empty document, because
 * the component holding them is not mounted unless real lines were read.
 *
 * `error` carries a real retry — which is also why the button that opens this
 * modal is NOT disabled while the history loads. A disabled control cannot offer
 * a retry, and the failed read is exactly the case a clinician needs one for.
 */
export type ReprescribeSource =
  | { state: 'loading' }
  | { state: 'error' }
  | { state: 'ready'; items: PrescribedItem[]; repeatFlag: boolean };

/**
 * The three sentences for the three answers that are not "here are the lines".
 *
 * Exported so the specs pin them: a modal that says nothing is the defect, so
 * "it says something true" is the thing being asserted, and an assertion against
 * a string typed into the test would not notice the copy being deleted.
 *
 * NONE of them says "try again" about something a retry cannot fix, and none
 * invents a reason — the read failed, or it came back with nothing, and those
 * are different sentences because they are different situations (I1: an absent
 * value is visibly flagged, never blanked into a dash).
 */
export const SOURCE_LOADING_COPY = 'Reading the prescription that needs correcting…';
export const SOURCE_ERROR_COPY =
  'The prescription on this order could not be read, so there are no lines to correct yet. Nothing has been changed. Try reading it again — the record has to load before it can be re-saved.';
export const SOURCE_EMPTY_COPY =
  'This order is flagged as needing its prescription re-saved, but the record came back with no prescribed lines on it. Nothing has been changed, and nothing has been guessed. Try reading it again — if it stays empty, raise it rather than prescribing around it.';

/**
 * The ids one line of the modal renders — one scheme, read by the controls and
 * by the error summary's links (2026-09-25).
 */
function represcribeLineIds(index: number) {
  return {
    line: `represcribe-${index}-line`,
    strength: `represcribe-${index}-variant`,
    strengthRetry: `represcribe-${index}-variant-retry`,
    directions: `represcribe-${index}-directions`,
    quantity: `represcribe-${index}-quantity`,
  };
}

/** The supplies section's id prefix inside the modal. */
const REPRESCRIBE_SUPPLY_IDS = supplySectionIds('represcribe-supplies');
const REPRESCRIBE_SUMMARY_ID = 'represcribe-error-summary';
const REPRESCRIBE_STILL_NEEDED_ID = 'represcribe-still-needed';

/** The summary's heading on this surface. Exported so specs assert the real string. */
export const REPRESCRIBE_SUMMARY_TITLE = 'Before you can re-save this prescription:';

/**
 * The re-prescribe modal for ONE order.
 *
 * ⚠️ NO HOOKS IN THIS FUNCTION, DELIBERATELY. It is a three-way branch on
 * {@link ReprescribeSource}, and the editors live in `RePrescribeForm` below so
 * that the state which seeds them cannot exist before the lines do.
 *
 * It rewrites the WHOLE prescription rather than one line, because that is what
 * the prescribe route does: it mints a new document from the items posted. The
 * existing lines are the starting point (directions and quantity carried over,
 * so a clinician's own words are not silently blanked) and every line must get a
 * strength chosen from the formulary before the confirm is reachable.
 */
export function RePrescribeModal({
  orderCaseId,
  productKey,
  orderState,
  source,
  onRetry,
  onDone,
  onCancel,
}: {
  orderCaseId: string;
  /** The ORDER's medication family — the server takes it from the order, so
   *  this is what the strength list must be read for. */
  productKey: string | null | undefined;
  /** Used ONLY to pick which true money sentence to show. Never a control. */
  orderState: string | null | undefined;
  /**
   * The stalled prescription — the starting point for the new one, AND the
   * honest answer when there is not one yet. See {@link ReprescribeSource}:
   * this is a state rather than an array precisely so "still loading" and
   * "the read failed" cannot arrive here disguised as "no lines".
   */
  source: ReprescribeSource;
  /** Re-run the read behind {@link source}. The error branch is useless without it. */
  onRetry: () => void;
  /**
   * Fired with the SERVER'S `pharmacySent`, never with an assumption.
   *
   * ⚠️ THIS PORTAL HAS CLAIMED A PHARMACY SEND IT DID NOT OBSERVE BEFORE, and it
   * is a specific, costly failure: on 2026-08-18 a prod dispense email was found
   * to have been going to the wrong mailbox for weeks precisely because every
   * internal signal looked healthy, and a clinician told "sent to the pharmacy"
   * has no reason to chase it. The prescribe response carries the fact; only it
   * may be reported.
   */
  onDone: (pharmacySent: boolean) => void;
  onCancel: () => void;
}) {
  /* THE SHELL IS OWNED HERE, AND THE STEP STATE WITH IT — deliberately.
     Every branch below renders the SAME `DialogShell` element in the SAME
     position, so React reconciles one dialog node across "loading", "could not
     be read" and the form. If each branch brought its own shell, the honest
     state giving way to the editors would UNMOUNT one `aria-modal` dialog and
     mount another: a second dialog announcement for a screen reader, and — via
     DialogShell's return-focus cleanup — an opener reference captured from a
     button that is being removed, so closing the dialog afterwards would drop
     focus to the body instead of returning it to "Re-save the prescription".
     `confirming` and `busy` live here because the TITLE and the escape route
     depend on them and nothing about them depends on the lines. */
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const ready = source.state === 'ready' && source.items.length > 0;
  return (
    <DialogShell
      title={confirming ? 'Re-save this prescription?' : 'Re-save this prescription'}
      onCancel={busy ? () => {} : onCancel}
      width="min(560px, 100%)"
    >
      {/* THE TWO BRANCHES THAT USED TO BE A DEAD DIALOG.
          Both keep the modal open and CANCELLABLE, both say what is actually
          true, and the failed one carries a retry — a clinician who cannot
          finish must at least be able to see why, and to try the one thing
          that would let him. */}
      {source.state === 'loading' ? (
        <>
          <p role="status" style={{ margin: '0 0 var(--gps-s-3)', fontSize: '14px', color: 'var(--gps-text)' }}>
            {SOURCE_LOADING_COPY}
          </p>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
          >
            Cancel
          </Button>
        </>
      ) : !ready ? (
        /* `ready` WITH NO LINES IS ITS OWN ANSWER, not a quieter version of the
           error. The read succeeded and the document carries nothing to
           correct — which an older orders build genuinely produces, because
           `StaffPrescription.items` is OPTIONAL on the wire. Saying "could not
           be read" about a read that worked would send a clinician chasing the
           wrong thing (I1). */
        <>
          <ErrorState
            message={source.state === 'error' ? SOURCE_ERROR_COPY : SOURCE_EMPTY_COPY}
            onRetry={onRetry}
            retryLabel="Try again"
          />
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
          >
            Cancel
          </Button>
        </>
      ) : (
        <RePrescribeForm
          orderCaseId={orderCaseId}
          productKey={productKey}
          orderState={orderState}
          items={source.items}
          repeatFlag={source.repeatFlag}
          confirming={confirming}
          setConfirming={setConfirming}
          busy={busy}
          setBusy={setBusy}
          onDone={onDone}
          onCancel={onCancel}
        />
      )}
    </DialogShell>
  );
}

/**
 * The editors themselves — MOUNTED ONLY WITH REAL LINES.
 *
 * ⚠️ THIS SEPARATION IS THE FIX, not a tidy-up. `drafts` is seeded in a one-shot
 * `useState` initializer, so whatever `items` is at MOUNT is what the clinician
 * gets for the life of the dialog; a later arrival never reaches it. Keeping the
 * initializer and moving the mount behind `source.state === 'ready' &&
 * items.length > 0` is what makes "opened before the read landed" impossible
 * rather than merely unlikely — the component holding the editors does not exist
 * until there is something for them to edit.
 */
function RePrescribeForm({
  orderCaseId,
  productKey,
  orderState,
  items,
  repeatFlag,
  confirming,
  setConfirming,
  busy,
  setBusy,
  onDone,
  onCancel,
}: {
  orderCaseId: string;
  productKey: string | null | undefined;
  orderState: string | null | undefined;
  /** Guaranteed non-empty by the caller — see the note above. */
  items: PrescribedItem[];
  repeatFlag: boolean;
  /**
   * The step, and whether a POST is in flight — OWNED BY THE PARENT.
   *
   * Not a preference: the dialog's TITLE changes on the confirm step and its
   * escape route is disabled while busy, and the parent renders the one shell
   * that carries both (see the note there for why there is only one). Holding
   * them here would mean the shell could not read them.
   */
  confirming: boolean;
  setConfirming: (next: boolean) => void;
  busy: boolean;
  setBusy: (next: boolean) => void;
  onDone: (pharmacySent: boolean) => void;
  onCancel: () => void;
}) {
  const { formulary, reload } = useFormulary(productKey ?? null);
  // THE SUPPLIES — the same read, seeding and gates as `PrescribeForm` (see the
  // file header for why this modal must carry them at all).
  const supplies = useSupplyRows(orderCaseId);
  // One draft per existing line. `variantKey` starts EMPTY on every line — a
  // pre-selected strength is a strength nobody chose, which is precisely what
  // the pricing anchor was.
  const [drafts, setDrafts] = useState(() =>
    items.map((item) => ({
      variantKey: '',
      variantName: '',
      // ⚠️ NEVER SEEDED WITH THE 'Not recorded' SENTINEL. Those words are in the
      // `dosage` column on live plans because the deleted auto-issue lane put
      // them there — nobody typed them — and pre-filling them here would invite
      // a clinician to press save and persist them onto a NEW, correctly
      // strengthed, signed prescription, turning a visible gap into something
      // that reads as a prescriber's instruction. Empty instead, and the form
      // will not submit until real directions are written.
      directions: seedDirections(item.dosage),
      quantity: item.quantity,
    })),
  );
  const [error, setError] = useState<string | null>(null);
  // The second double-submit guard. A ref, not `busy`: two clicks in one tick
  // both read the pre-update state value.
  const inFlight = useRef(false);
  /**
   * WHAT WAS MISSING AT THE LAST BLOCKED "Continue" (2026-09-25) — the same
   * mechanism as `PrescribeForm`: the inline errors and the summary show what
   * is missing NOW within this set, so each clears as its field is filled.
   */
  const [flagged, setFlagged] = useState<ReadonlySet<string> | null>(null);
  const [blockedPresses, setBlockedPresses] = useState(0);
  const summaryRef = useRef<HTMLDivElement>(null);

  const paymentTaken = paymentTakenForCopy(orderState);
  const money = chargeCopy(paymentTaken);

  function patch(index: number, next: Partial<(typeof drafts)[number]>) {
    setDrafts((prev) => prev.map((d, i) => (i === index ? { ...d, ...next } : d)));
  }

  /**
   * ⚠️ A STRENGTH IS REQUIRED TO SUBMIT, ON EVERY LINE. This is the rule the
   * whole slice exists to enforce, stated here as a value rather than a UI
   * nicety: with any `variantKey` blank there is nothing to confirm, and the
   * server would refuse it anyway (I1 — no prescription without a
   * prescriber-chosen strength, on any path).
   */
  const ready =
    /* `drafts.length > 0` is now BELT-AND-BRACES, not the gate. It used to be
       the only thing standing between a clinician and a permanently disabled
       Continue: with no lines read it was false and nothing on the screen said
       why. The caller no longer mounts this component without lines, so this
       clause should be unreachable — it stays because a gate that silently
       became decorative is still the right answer if the invariant ever slips. */
    drafts.length > 0 &&
    drafts.every(
      (d) =>
        d.variantKey.trim() !== '' &&
        d.directions.trim() !== '' &&
        directionsWithinLimit(d.directions) &&
        Number.isInteger(d.quantity) &&
        d.quantity >= 1,
    ) &&
    // ⚠️ AND THE SUPPLIES. Continue and Re-save wait while the read loads and
    // stay blocked when it failed — a re-saved document that silently carried
    // no supplies is the defect this section was added to close.
    supplies.valid;

  // WHAT IS STILL MISSING, IN WORDS — the `ready` clauses above, named. The
  // medication is the stored line's, with no control here and no clause in
  // `ready`, so it is not asked about (`medicationEditable: false`).
  const missing: MissingItem[] = [
    ...drafts.flatMap((draft, index) =>
      lineRequirements(
        {
          productKey: items[index].productKey,
          medicationName: items[index].medicationName,
          variantKey: draft.variantKey,
          directions: draft.directions,
          quantity: draft.quantity,
        },
        index,
        represcribeLineIds(index),
        { medicationEditable: false },
      ),
    ),
    ...supplyRequirements(supplies.context, supplies.rows, REPRESCRIBE_SUPPLY_IDS),
  ];
  const errors = shownErrors(missing, flagged);

  useEffect(() => {
    if (blockedPresses === 0) return;
    const summary = summaryRef.current;
    if (!summary) return;
    summary.focus({ preventScroll: true });
    summary.scrollIntoView?.({ block: 'center' });
  }, [blockedPresses]);

  /**
   * "Continue" — PRESSABLE WHILE INCOMPLETE, and safe to be: it moves no data,
   * it only swaps this dialog's body for the confirm step. Incomplete → the
   * error summary, and focus to it; complete → the confirm step, exactly as
   * before. The POST lives behind "Re-save and send", which keeps its own
   * `disabled={busy || !ready}` and the in-flight ref.
   *
   * ⚠️ A PRESS CLEARS A STALE SERVER REFUSAL, on both branches — as
   * `PrescribeForm.handleSubmit` does. "Back" deliberately KEEPS the refusal on
   * screen (it tells the clinician what to fix while they edit), but once they
   * press Continue again it describes a form that no longer exists: left up
   * beside the summary it is a second `role="alert"` (the summary is this
   * view's one assertive region) and an out-of-date sentence above the list of
   * what is missing; carried to the confirm step it reads as a refusal of
   * edits nobody has sent yet (release review 2026-09-25).
   */
  function pressContinue() {
    if (busy) return;
    setError(null);
    if (!ready) {
      setFlagged(new Set(missing.map((item) => item.key)));
      setBlockedPresses((n) => n + 1);
      return;
    }
    setFlagged(null);
    setConfirming(true);
  }

  async function submit() {
    if (inFlight.current || !ready || !supplies.rows) return;
    inFlight.current = true;
    setBusy(true);
    setError(null);
    try {
      const body: PrescriptionItemInput[] = drafts.map((d, i) => ({
        productKey: items[i].productKey,
        medicationName: items[i].medicationName,
        dosage: d.directions.trim(),
        quantity: d.quantity,
        // Only the KEY goes on the wire. The server resolves it against the same
        // formulary and snapshots what IT says; the contract rejects a
        // client-posted strength or name outright (R41).
        variantKey: d.variantKey.trim(),
      }));
      // `supplies` ALWAYS rides the body — `[]` included. An ABSENT key tells
      // orders to carry the previous prescription's supplies forward, and for a
      // backlog document that is NULL: the re-save would go out with none.
      const result = await prescribe(orderCaseId, {
        items: body,
        repeatFlag,
        supplies: suppliesForWire(supplies.rows),
      });
      setConfirming(false);
      onDone(result.pharmacySent);
    } catch (cause) {
      /* ⚠️ STAY ON THE CONFIRM STEP. The refusal is shown where the clinician
         just clicked, not on a screen they have to navigate back to and then
         wonder whether anything happened. Every choice they made is still in the
         form behind it, reachable with "Back" — a refusal is not a reason to
         throw somebody's work away, and several of these refusals (an
         already-sent supply, an uncaptured payment) mean an identical retry
         refuses identically, so the useful next move is reading the sentence
         rather than resubmitting. */
      setError(refusalCopy(cause));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }

  const chosenNames = drafts.map((d) => d.variantName).filter((n) => n !== '');
  // What the confirm step lists — the server's names beside the quantities.
  const supplyLines =
    supplies.rows && supplies.context.state === 'ready' ? suppliesSummary(supplies.rows, supplies.context.context) : [];

  return (
    /* ONE DIALOG, TWO STEPS — deliberately NOT a ConfirmDialog nested inside the
       parent's shell. Two stacked overlays give a screen reader two `aria-modal`
       dialogs at once, and the inner overlay's click bubbles into the outer
       one's cancel, so dismissing the confirm would also throw away the
       clinician's typed directions. The confirm REPLACES the body instead, and
       "Back" returns to it with everything still filled in. The shell itself is
       the parent's — this renders only what goes inside it. */
    <>
      {/* THE MONEY SENTENCE IS ON BOTH STEPS. On the first it is read while the
          decision is being formed; on the CONFIRM step it is the last thing
          read before a signed document is minted. A sentence shown only on the
          screen behind the confirm has been read before the decision, not
          while taking it. */}
      {confirming ? (
        <>
          {chosenNames.length > 0 ? (
            <div style={{ marginBottom: 'var(--gps-s-3)' }}>
              <p style={{ margin: '0 0 6px', fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)' }}>
                What the pharmacist will read
              </p>
              {/* The FULL licensed description, not a key and not a shortened
                  label: this exact string is what goes on the document. */}
              {chosenNames.map((name) => (
                <p key={name} style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: 600, color: 'var(--gps-text)' }}>
                  {name}
                </p>
              ))}
            </div>
          ) : null}
          {/* THE SUPPLIES that will go out with the new document — or, in words,
              that none will. The last screen before a signed document is minted. */}
          {supplyLines.length > 0 ? (
            <div style={{ marginBottom: 'var(--gps-s-3)', fontSize: '14px' }}>
              <p style={{ margin: '0 0 4px', fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)' }}>
                Supplies to include
              </p>
              <SupplyLines supplies={supplyLines} label="Supplies to include" />
            </div>
          ) : (
            <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '14px', color: 'var(--gps-text)' }}>{NO_SUPPLIES_COPY}</p>
          )}
          <Alert tone="info" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
            <strong style={{ display: 'block', fontWeight: 600 }}>{money}</strong>
          </Alert>
          <p style={{ margin: '0 0 var(--gps-s-3)', fontSize: '14px', color: 'var(--gps-text)' }}>
            {/* WHAT ACTUALLY HAPPENS, AND NOTHING MORE (2026-09-13). This used to say the document
                was "sent to the pharmacy". It is not: a correction runs send-free — no dispensing
                request is transmitted and no email is raised, which is the CTO's explicit rule for
                an order the pharmacy already has. What the correction DOES do is supersede the
                strength-less document and put the order back in the pharmacy's own queue, where
                they read the corrected item. Telling a prescriber their document was transmitted
                when it was not is the same class of defect as printing a strength nobody chose. */}
            A new prescription is issued and the document that recorded no strength is superseded.{' '}
            {CORRECTION_PHARMACY_EFFECT}
          </p>
          {error ? <ErrorLine>{error}</ErrorLine> : null}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <Button
              type="button"
              variant="cta"
              disabled={busy || !ready}
              onClick={() => void submit()}
              style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
            >
              {busy ? 'Saving…' : 'Re-save and send'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => setConfirming(false)}
              style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
            >
              Back
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* THE REASON, first — a clinician who opened this from a chip still
              needs to read what is wrong with the record before touching it. */}
          <Alert tone="danger" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
            <strong style={{ display: 'block', fontWeight: 600 }}>
              This prescription was not saved with a medication strength.
            </strong>
            <span style={{ display: 'block', marginTop: '4px' }}>
              Nothing has been inferred or filled in, and nothing can be dispensed against it. Choose the strength you
              are prescribing and save it again — {CORRECTION_PHARMACY_EFFECT_SHORT}.
            </span>
          </Alert>

          <Alert tone="info" role="status" style={{ marginBottom: 'var(--gps-s-3)' }}>
            <strong style={{ display: 'block', fontWeight: 600 }}>{money}</strong>
          </Alert>

          {/* ONE legend, above the fields — AFTER the reason and the money
              sentence, which must be read before anything is touched. */}
          <RequiredLegend />

          {drafts.map((draft, index) => {
            const ids = represcribeLineIds(index);
            const directionsError = inlineErrorFor(errors, `line-${index}-directions`);
            const quantityError = inlineErrorFor(errors, `line-${index}-quantity`);
            return (
              <div
                key={index}
                id={ids.line}
                // The summary's focus target of last resort (a strength list
                // still loading has no control to land on). Never tabbable.
                tabIndex={-1}
                style={{
                  border: '1px solid var(--gps-border)',
                  borderRadius: 'var(--gps-r-md)',
                  padding: 'var(--gps-s-3)',
                  marginBottom: 'var(--gps-s-3)',
                }}
              >
                {drafts.length > 1 ? (
                  <p style={{ margin: '0 0 6px', fontSize: '12.5px', fontWeight: 600, color: 'var(--gps-muted)' }}>
                    Line {index + 1}
                  </p>
                ) : null}
                <VariantPicker
                  id={ids.strength}
                  formulary={formulary}
                  value={draft.variantKey}
                  onChange={(variantKey, variant) => patch(index, { variantKey, variantName: variant?.name ?? '' })}
                  onRetry={reload}
                  disabled={busy}
                  required
                  error={inlineErrorFor(errors, `line-${index}-strength`)}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--gps-s-3)', marginTop: '8px' }}>
                  <Field
                    label="Directions"
                    htmlFor={ids.directions}
                    required
                    error={directionsError}
                    errorId={`${ids.directions}-error`}
                    errorAlert={false}
                  >
                    <TextInput
                      id={ids.directions}
                      value={draft.directions}
                      disabled={busy}
                      aria-required="true"
                      aria-invalid={directionsError ? true : undefined}
                      aria-describedby={
                        directionsError ? `${ids.directions}-error ${ids.directions}-count` : `${ids.directions}-count`
                      }
                      style={invalidControlStyle(directionsError)}
                      onChange={(e) => patch(index, { directions: e.target.value })}
                    />
                    <DirectionsCounter id={`${ids.directions}-count`} value={draft.directions} />
                  </Field>
                  <Field
                    label="Quantity"
                    htmlFor={ids.quantity}
                    required
                    error={quantityError}
                    errorId={`${ids.quantity}-error`}
                    errorAlert={false}
                  >
                    <TextInput
                      id={ids.quantity}
                      type="number"
                      min={1}
                      step={1}
                      inputMode="numeric"
                      value={String(draft.quantity)}
                      disabled={busy}
                      aria-required="true"
                      aria-invalid={quantityError ? true : undefined}
                      aria-describedby={quantityError ? `${ids.quantity}-error` : undefined}
                      style={invalidControlStyle(quantityError)}
                      onChange={(e) => patch(index, { quantity: e.target.value === '' ? 0 : Number(e.target.value) })}
                    />
                  </Field>
                </div>
              </div>
            );
          })}

          {/* SUPPLIES — below the strength lines, exactly as on the ordinary form. */}
          <SuppliesSection
            context={supplies.context}
            rows={supplies.rows}
            disabled={busy}
            onChange={supplies.setRows}
            onRetry={supplies.reload}
            idBase={REPRESCRIBE_SUPPLY_IDS.section}
          />

          {error ? <ErrorLine>{error}</ErrorLine> : null}

          {/* THE ERROR SUMMARY, right above Continue, after a blocked press. */}
          <ErrorSummary ref={summaryRef} id={REPRESCRIBE_SUMMARY_ID} title={REPRESCRIBE_SUMMARY_TITLE} items={errors} />

          <div style={{ display: 'flex', gap: '10px', marginTop: '8px', flexWrap: 'wrap' }}>
            {/* WAITS until EVERY line has a chosen strength, directions and a
                quantity AND the supplies have loaded with valid quantities —
                see `ready`. `aria-disabled`, not `disabled`, while that is all
                it is waiting on: see `pressContinue`. */}
            <Button
              type="button"
              variant="cta"
              disabled={busy}
              aria-disabled={!busy && !ready ? true : undefined}
              aria-describedby={!busy && !ready && missing.length > 0 ? REPRESCRIBE_STILL_NEEDED_ID : undefined}
              onClick={pressContinue}
              style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
            >
              Continue
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={onCancel}
              style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }}
            >
              Cancel
            </Button>
          </div>
          {!busy && !ready ? <StillNeededHint id={REPRESCRIBE_STILL_NEEDED_ID} items={missing} /> : null}
          <Help style={{ marginTop: '12px' }}>
            This is a temporary screen for orders prescribed before the strength list existed. Ordinary prescribing
            happens on the Decision card.
          </Help>
        </>
      )}
    </>
  );
}
